import React, { useState, useEffect } from 'react';
import { Card, Text, Button, ProgressBar, Badge, Tab, TabList } from '@fluentui/react-components';
import {
    CalendarMonth24Regular,
    People24Regular,
    Clock24Regular,
    Add24Regular,
    CalendarLtr24Regular,
} from '@fluentui/react-icons';
import { apiService } from '../../../services/api/apiService';
import ScheduleManagement from './ScheduleManagement';

interface ConsultationMetrics {
    todaysConsultations: { completed: number; total: number };
    activeNow: number;
    pendingAssignments: number;
    doctorAvailability: { available: number; total: number };
    emergencyCalls: number;
}

interface Patient {
    id: string;
    name: string;
    mrn: string;
    language: string;
    assignedDoctor: string;
    status: 'in progress' | 'scheduled' | 'new' | 'done' | 'emergency';
    consultationType: string;
}

interface SupportStaffDashboardProps {
    activeTab?: string;
}

export const SupportStaffDashboard: React.FC<SupportStaffDashboardProps> = ({
    activeTab: propActiveTab,
}) => {
    console.log('SupportStaffDashboard component is executing!');
    const [metrics, setMetrics] = useState<ConsultationMetrics | null>(null);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [loading, setLoading] = useState(true);
    // Use propActiveTab if present, otherwise use internal state
    const [internalTab, setInternalTab] = useState<string>('overview');
    const activeTab = propActiveTab ?? internalTab;

    useEffect(() => {
        if (activeTab === 'overview') {
            loadData();
        }
    }, [activeTab]);

    const loadData = async () => {
        try {
            setLoading(true);

            // Fetch metrics and patients from backend
            const [metricsData, patientsData] = await Promise.all([
                apiService.getDashboardMetrics(),
                apiService.getPatients(),
            ]);

            setMetrics(metricsData);
            setPatients(patientsData as Patient[]);
        } catch (error) {
            console.error('Error loading dashboard data:', error);
            // Fallback to static data if backend fails
            setMetrics({
                todaysConsultations: { completed: 24, total: 32 },
                activeNow: 6,
                pendingAssignments: 8,
                doctorAvailability: { available: 12, total: 18 },
                emergencyCalls: 1,
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'in progress':
                return (
                    <Badge appearance="filled" color="brand">
                        In Progress
                    </Badge>
                );
            case 'scheduled':
                return (
                    <Badge appearance="tint" color="success">
                        Scheduled
                    </Badge>
                );
            case 'new':
                return (
                    <Badge appearance="outline" color="warning">
                        New
                    </Badge>
                );
            case 'emergency':
                return (
                    <Badge appearance="filled" color="danger">
                        Emergency
                    </Badge>
                );
            case 'done':
                return (
                    <Badge appearance="ghost" color="subtle">
                        Done
                    </Badge>
                );
            default:
                return <Badge appearance="outline">Unknown</Badge>;
        }
    };

    const renderOverviewTab = () => {
        if (loading) {
            return <div>Loading dashboard...</div>;
        }

        if (!metrics) {
            return <div>Error loading dashboard data</div>;
        }

        return (
            <div>
                <div style={{ marginBottom: '24px' }}>
                    <Text size={900} weight="bold">
                        Support Staff Dashboard
                    </Text>
                    <Text
                        size={300}
                        style={{ display: 'block', color: '#605e5c', marginTop: '4px' }}
                    >
                        {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        })}{' '}
                        at{' '}
                        {new Date().toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Text>
                </div>

                {/* Metrics Cards */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '20px',
                        marginBottom: '32px',
                    }}
                >
                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <CalendarMonth24Regular
                                style={{ fontSize: '32px', color: '#0078d4' }}
                            />
                            <div style={{ flex: 1 }}>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Today's Consultations
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.todaysConsultations.completed}/
                                    {metrics.todaysConsultations.total}
                                </Text>
                                <Text size={200} style={{ color: '#605e5c' }}>
                                    Completed
                                </Text>
                                <ProgressBar
                                    value={
                                        metrics.todaysConsultations.completed /
                                        metrics.todaysConsultations.total
                                    }
                                    style={{ marginTop: '8px' }}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Clock24Regular style={{ fontSize: '32px', color: '#107c10' }} />
                            <div style={{ flex: 1 }}>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Active Now
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.activeNow}
                                </Text>
                                <Text size={200} style={{ color: '#605e5c' }}>
                                    Ongoing consultations
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Pending Assignments
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.pendingAssignments}
                                </Text>
                                <Button
                                    size="small"
                                    appearance="primary"
                                    style={{ marginTop: '8px' }}
                                    onClick={() => setInternalTab('schedule')}
                                >
                                    Review Queue
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <Card style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <People24Regular style={{ fontSize: '32px', color: '#8764b8' }} />
                            <div style={{ flex: 1 }}>
                                <Text size={300} style={{ color: '#605e5c' }}>
                                    Doctor Availability
                                </Text>
                                <Text size={700} weight="bold" style={{ display: 'block' }}>
                                    {metrics.doctorAvailability.available}/
                                    {metrics.doctorAvailability.total}
                                </Text>
                                <Text size={200} style={{ color: '#605e5c' }}>
                                    Available now
                                </Text>
                                <ProgressBar
                                    value={
                                        metrics.doctorAvailability.available /
                                        metrics.doctorAvailability.total
                                    }
                                    style={{ marginTop: '8px' }}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Patients List */}
                <Card style={{ padding: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '20px',
                        }}
                    >
                        <Text size={600} weight="semibold">
                            Recent Patients ({patients.length})
                        </Text>
                        <Button appearance="primary" icon={<Add24Regular />}>
                            New Consultation Request
                        </Button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e1e5e9' }}>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Patient
                                    </th>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        MRN
                                    </th>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Language
                                    </th>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Doctor
                                    </th>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Consultation Type
                                    </th>
                                    <th
                                        style={{
                                            textAlign: 'left',
                                            padding: '12px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {patients.map((patient) => (
                                    <tr
                                        key={patient.id}
                                        style={{ borderBottom: '1px solid #f3f2f1' }}
                                    >
                                        <td style={{ padding: '12px' }}>
                                            <Text size={400} weight="semibold">
                                                {patient.name}
                                            </Text>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <Text size={300}>{patient.mrn}</Text>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <Text size={300}>{patient.language}</Text>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <Text size={300}>{patient.assignedDoctor}</Text>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <Text size={300}>{patient.consultationType}</Text>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            {getStatusBadge(patient.status)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        );
    };

    // Remove debug banners and try/catch, return normal dashboard content
    return (
        <div style={{ padding: '0' }}>
            <TabList
                selectedValue={activeTab}
                onTabSelect={(event, data) => {
                    if (!propActiveTab) setInternalTab(data.value as string);
                }}
                style={{ marginBottom: '24px' }}
            >
                <Tab value="overview" icon={<CalendarMonth24Regular />}>
                    Dashboard Overview
                </Tab>
                <Tab value="schedule" icon={<CalendarLtr24Regular />}>
                    Schedule Management
                </Tab>
                <Tab value="doctors" icon={<People24Regular />}>
                    Doctors
                </Tab>
                <Tab value="settings" icon={<Add24Regular />}>
                    Settings
                </Tab>
            </TabList>

            {/* Tab Content */}
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'schedule' && <ScheduleManagement />}
            {activeTab === 'doctors' && (
                <div style={{ padding: '24px' }}>
                    <Text size={600} weight="semibold">
                        Doctors Management
                    </Text>
                    <div style={{ marginTop: '16px' }}>
                        <Text size={300}>
                            This is a placeholder for the Doctors management page.
                        </Text>
                    </div>
                </div>
            )}
            {activeTab === 'settings' && (
                <div style={{ padding: '24px' }}>
                    <Text size={600} weight="semibold">
                        Settings
                    </Text>
                    <div style={{ marginTop: '16px' }}>
                        <Text size={300}>This is a placeholder for the Settings page.</Text>
                    </div>
                </div>
            )}
        </div>
    );
};
